import { z } from "zod";

export const CAREGIVER_NAME_MAX = 120;
export const CAREGIVER_EMAIL_MAX = 254;

/**
 * Visible sections boolean map. The server enforces this — the client cannot
 * bypass section filtering even if it sets all keys to true and strips the
 * flag checks in JS, because the server's scopeStripper is the real gate.
 */
export const visibleSectionsSchema = z.object({
  basic: z.boolean().default(true),
  emergencyContacts: z.boolean().default(false),
  allergies: z.boolean().default(false),
  medications: z.boolean().default(false),
  carePreferences: z.boolean().default(false),
  routines: z.boolean().default(false),
  feeding: z.boolean().default(false),
  medical: z.boolean().default(false),
  // Pickup & authorization: who may collect the child, and whether a caregiver
  // may consent to emergency treatment. Defaults to false like every non-basic
  // section — and because links issued BEFORE this key existed have no `pickup`
  // property in their stored JSONB, `sections.pickup` reads undefined there and
  // the section stays hidden. Already-issued links can never start leaking it.
  pickup: z.boolean().default(false),
  // The child's optional legal name (v2, BRIEF §5.2). Not a section of its own
  // — the nickname is always shown — but an explicit opt-in, unticked in the
  // share dialog. Fail-closed exactly like `pickup`: a link issued before this
  // key existed has no `legalName` property in its stored JSONB, so it reads
  // undefined and the legal name stays off that link forever. scopeStripper
  // additionally requires `=== true`, so nothing but a real `true` opens it.
  legalName: z.boolean().default(false),
});

export type VisibleSections = z.infer<typeof visibleSectionsSchema>;

/**
 * Expiration presets exposed to the UI. The backend always stores an
 * absolute `expiresAt` timestamp (or null for "never"). The client sends one
 * of these preset keys; the route handler converts it to a timestamp.
 */
export const EXPIRATION_PRESETS = ["24h", "7d", "30d", "never"] as const;
export type ExpirationPreset = (typeof EXPIRATION_PRESETS)[number];

export const EXPIRATION_PRESET_MS: Record<Exclude<ExpirationPreset, "never">, number> = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

export const createSharedLinkSchema = z.object({
  childId: z.string().uuid("Invalid child id"),
  caregiverName: z
    .string()
    .trim()
    .min(1, "Caregiver name is required")
    .max(CAREGIVER_NAME_MAX, "Caregiver name is too long"),
  caregiverEmail: z
    .string()
    .trim()
    .max(CAREGIVER_EMAIL_MAX, "Caregiver email is too long")
    .email("Invalid email address")
    .nullable()
    .optional()
    .transform((v) => (v && v.length > 0 ? v : null)),
  visibleSections: visibleSectionsSchema,
  expirationPreset: z.enum(EXPIRATION_PRESETS).default("7d"),
});

export type CreateSharedLinkInput = z.infer<typeof createSharedLinkSchema>;

export const SHARE_TOKEN_REGEX = /^[A-Za-z0-9_-]{32}$/;
